import { useQuery } from '..';
import { useState, useRef } from '..';
import { React } from 'libs/react';
import { mergeDeep } from '@apollo/client/utilities';
import { useEffect } from 'libs/react';
import { UnexpectedError } from 'libs/error/UnexpectedError';
import { SessionStorage } from 'libs/storage/SessionStorage';
/**
 * @function usePagedQuery
 * @since 1.0.0
 */
export function usePagedQuery(query, count, options) {
    var _a, _b;
    if (options == null) {
        options = {};
    }
    const { length, objectType, useQueryCount } = Object.assign({ length: 10, objectType: '', useQueryCount: true }, options.paging);
    delete options.paging;
    const args = JSON.stringify(options);
    // Generate a unique key for this pagination state based on objectType and query variables
    const paginationKey = React.useMemo(() => {
        const keyParts = [objectType || 'default'];
        // Include variables but exclude pagination-related fields
        if (options === null || options === void 0 ? void 0 : options.variables) {
            const cleanVariables = Object.assign({}, options.variables);
            if (cleanVariables.input) {
                const cleanInput = Object.assign({}, cleanVariables.input);
                delete cleanInput.after;
                delete cleanInput.limit;
                keyParts.push(JSON.stringify(cleanInput));
            }
        }
        return `pagination.${keyParts.join('.')}`;
    }, [objectType, args]);
    const [complete, setComplete] = useState(false);
    const [fetching, setFetching] = useState(false);
    // Initialize page from session storage on mount
    // When the component remounts with the same paginationKey, it will restore the saved page
    const [page, setPage] = useState(() => {
        const savedPage = SessionStorage.get(paginationKey, null);
        return savedPage && typeof savedPage === 'number' && savedPage > 0 ? savedPage : 1;
    });
    const [data, ready, error, result] = useQuery(query, merge(options, {
        fetchPolicy: (_a = options === null || options === void 0 ? void 0 : options.fetchPolicy) !== null && _a !== void 0 ? _a : 'cache-first',
        nextFetchPolicy: 'cache-first',
        variables: {
            input: {
                after: length * (page - 1),
                limit: length
            }
        }
    }));
    // TODO : need to support useCount during refactoring
    let total = 0;
    if (!useQueryCount) {
        total = (_b = data === null || data === void 0 ? void 0 : data[objectType]) === null || _b === void 0 ? void 0 : _b.count;
    }
    else {
        total = useCount(count, options);
    }
    useEffect(() => {
        if (ready && total >= 0) {
            setComplete(true);
        }
        setFetching(ready == false);
    }, [ready, total]);
    // Track previous args to detect actual changes (not initial mount)
    const prevArgsRef = useRef(null);
    useEffect(() => {
        /*
         * Go back to the first page when something
         * inside the query changes (but not on initial mount).
         */
        if (prevArgsRef.current === null) {
            // Initial mount - don't reset, let the saved page be used
            prevArgsRef.current = args;
            return;
        }
        if (prevArgsRef.current !== args) {
            // Query variables changed - reset to page 1
            prevArgsRef.current = args;
            setPage(1);
            SessionStorage.set(paginationKey, 1);
        }
        /*
         * Do not clamp the page here because the page count query, which
         * is used to clamp might not have been executed yet. Lets assume
         * there are at least 2 pages, otherwise no big deal.
         */
        /*client.query(
          merge(options, {
            query: query,
            variables: {
              input: {
                after: length * 1,
                limit: length
              }
            }
          })
        ); */ // TODO: Fix this (commenting out for now (performance issue)))
    }, [args, paginationKey]);
    /**
     * @function gotoNextPage
     * @since 1.0.0
     */
    function gotoNextPage() {
        if (total == null) {
            return;
        }
        const next = clamp(page + 1);
        if (next == page) {
            return;
        }
        setPage(next);
        SessionStorage.set(paginationKey, next);
    }
    /**
     * @function gotoPrevPage
     * @since 1.0.0
     */
    function gotoPrevPage() {
        if (total == null) {
            return;
        }
        const prev = clamp(page - 1);
        if (prev == page) {
            return;
        }
        setPage(prev);
        SessionStorage.set(paginationKey, prev);
    }
    /**
     * @function gotoPage
     * @since 1.0.0
     */
    function gotoPage(p) {
        if (total == null) {
            return;
        }
        const next = clamp(p);
        if (next == page) {
            return;
        }
        setPage(next);
        SessionStorage.set(paginationKey, next);
    }
    let minPage = 1;
    let maxPage = 1;
    if (count) {
        minPage = 1;
        maxPage = Math.ceil(total / length);
    }
    /**
     * @function clamp
     * @since 1.0.0
     */
    function clamp(page) {
        if (page < minPage)
            page = minPage;
        if (page > maxPage)
            page = maxPage;
        return page;
    }
    const hasNext = page < maxPage;
    const hasPrev = page > minPage;
    let resultData = {};
    if (data && objectType != '') {
        resultData[objectType] = data[objectType].items.map((item) => {
            return item;
        });
    }
    else {
        resultData = data;
    }
    return [
        resultData,
        complete,
        error,
        result,
        fetching,
        gotoNextPage,
        gotoPrevPage,
        gotoPage,
        hasNext,
        hasPrev,
        page > maxPage ? maxPage : page,
        maxPage,
        total
    ];
}
/**
 * @function useCount
 * @since 1.0.0
 */
function useCount(query, options) {
    const [data, ready] = useQuery(query, options);
    if (ready && data) {
        const [field] = Object.keys(data);
        if (field == null) {
            throw new UnexpectedError();
        }
        return data[field];
    }
    return null;
}
/**
 * @function merge
 * @since 1.0.0
 * @hidden
 */
function merge(source, values) {
    return mergeDeep(source, values);
}
